import { getBasePath } from "@/lib/utils";
import { RootState } from "@/redux/store";
import {
  useGetPdcByInstallmentIdQuery,
  useUpdateInstallmentCalculationMutation,
} from "@/services";
import { useDownloadReceipt } from "@/services/school/commercial/installment/payment/useDownloadReceiptMutation";
import { useGetReceiptByInstallmentIdQuery } from "@/services/school/commercial/installment/payment/useGetReceiptByInstallmentIdQuery";
import { useGetInstallmentByInstallmentIdQuery } from "@/services/school/commercial/installment/useGetInstallmentByInstallmentIdQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import {
  EXPECTED_DUE_DATE,
  GENERATED_BY,
  GENERATED_DATE,
  GST_PERCENTAGE,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_AMOUNT_GST,
  INSTALLMENT_AMOUNT_NON_GST,
  INSTALLMENT_NUMBER,
  TDS,
  TOTAL_INVOICE_AMOUNT,
  YEAR
} from "./consts";

// Form validation schema
const schema = z.object({
  // Installment Calculation
  [YEAR]: z.string().optional(),
  [INSTALLMENT_NUMBER]: z.string().min(1, "Installment number is required"),
  [TDS]: z.boolean().optional(),
  [EXPECTED_DUE_DATE]: z.string().optional(),
  [INSTALLMENT_AMOUNT]: z.string().optional(),
  [GST_PERCENTAGE]: z.string().optional(),
  [INSTALLMENT_AMOUNT_NON_GST]: z.string().optional(),
  [INSTALLMENT_AMOUNT_GST]: z.string().optional(),
  [TOTAL_INVOICE_AMOUNT]: z.string().optional(),
});

export type FormValues = z.infer<typeof schema>;

interface UseEditInstallmentScheduleProps {
  schoolId: string;
  installmentIndex: string;
}

const useEditInstallmentSchedule = ({
  schoolId,
  installmentIndex,
}: UseEditInstallmentScheduleProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const attachmentRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const isCRM_HEAD = loggedInUserData.role === "CRM_HEAD";

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Installment Calculation
      [YEAR]: "",
      [INSTALLMENT_NUMBER]: "",
      [TDS]: false,
      [EXPECTED_DUE_DATE]: "",
      [INSTALLMENT_AMOUNT]: "",
      [GST_PERCENTAGE]: "",
      [INSTALLMENT_AMOUNT_NON_GST]: "",
      [INSTALLMENT_AMOUNT_GST]: "",
      [TOTAL_INVOICE_AMOUNT]: "",
    },
  });

  const receiptForm = useForm<any>({
    // resolver: zodResolver(any),
    defaultValues: {
      // Installment Calculation
      [GENERATED_BY]: "",
      [GENERATED_DATE]: "",
    },
  });

  const requiredFields = ["gstNotes", "installmentNumber"];

  // PDC Details state and query
  const [isPdcCheckEnabled, setIsPdcCheckEnabled] = useState(false);
  const { data: pdcData, isLoading: isPdcLoading } =
    useGetPdcByInstallmentIdQuery(installmentIndex || "", {
      enabled: !!installmentIndex,
    });

  const handlePdcCheck = (checked: boolean) => {
    setIsPdcCheckEnabled(checked);
  };
  const { mutateAsync: donwloadReceipt } = useDownloadReceipt(installmentIndex);

  const { data: installmentsResp } = useGetInstallmentByInstallmentIdQuery(
    installmentIndex || "",
    { enabled: !!installmentIndex } as any
  );

  const { data: receiptResp } = useGetReceiptByInstallmentIdQuery(
    installmentIndex || "",
    { enabled: !!installmentIndex } as any
  );

  useEffect(() => {
    if (receiptResp?.data) {
      receiptForm.reset({
        [GENERATED_BY]: receiptResp?.data?.generatedByProfile?.name,
        [GENERATED_DATE]: receiptResp?.data?.generationDate,
      });
    }
  }, [receiptResp]);

  useEffect(() => {
    if (!installmentsResp?.data) return;

    const installment = installmentsResp.data;

    if (installment) {
      const installmentAmount = Number(installment.installmentAmount || 0);
      const gstNotes = installment.gstNotes || "";

      // Calculate GST and Non-GST amounts if not present
      const installmentAmountNonGst =
        installment.installmentAmountNonGst !== undefined
          ? String(installment.installmentAmountNonGst)
          : String(installmentAmount);

      const installmentAmountGst =
        installment.installmentAmountGst !== undefined
          ? String(installment.installmentAmountGst)
          : String(((installmentAmount * 1) / 100).toFixed(2));

      const totalInvoiceAmount =
        installment.totalInvoiceAmount !== undefined
          ? String(installment.totalInvoiceAmount)
          : String(
              (
                installmentAmount +
                (installmentAmount * 1) / 100
              ).toFixed(2)
            );

      form.reset({
        [YEAR]: String(installment.year || ""),
        [INSTALLMENT_NUMBER]: String(installment.installmentNumber || ""),
        [TDS]: Boolean(installment.tds),
        [EXPECTED_DUE_DATE]: installment.expectedDueDate || "",
        [INSTALLMENT_AMOUNT]: String(installmentAmount),
        [GST_PERCENTAGE]: String(gstNotes),
        [INSTALLMENT_AMOUNT_NON_GST]: installmentAmountNonGst,
        [INSTALLMENT_AMOUNT_GST]: installmentAmountGst,
        [TOTAL_INVOICE_AMOUNT]: String(installmentAmount),
      });
    }
  }, [installmentsResp, installmentIndex, form]);

  const watchedState = form.watch(GST_PERCENTAGE);

  useEffect(() => {
    const installmentAmount = Number(form.getValues(INSTALLMENT_AMOUNT) || 0);
    const gstNotes = watchedState || "";
    if (!installmentAmount) return;

    const installmentAmountNonGst = String(installmentAmount);
    const installmentAmountGst = String(
      ((installmentAmount * 1) / 100).toFixed(2)
    );
    const totalInvoiceAmount = String(installmentAmount);
    form.setValue(INSTALLMENT_AMOUNT_NON_GST, installmentAmountNonGst);
    form.setValue(INSTALLMENT_AMOUNT_GST, installmentAmountGst);
    form.setValue(TOTAL_INVOICE_AMOUNT, totalInvoiceAmount);
  }, [watchedState, form]);

  const { mutateAsync: updateInstallment } = useUpdateInstallmentCalculationMutation({
    onSuccess: () => {
      setIsLoading(false);
      queryClient.invalidateQueries({ queryKey: ["installment-calc-details"] });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const onSubmit = async (data: FormValues) => {
    console.log(data);
    if (!id || !schoolId || !installmentIndex) return;
    setIsLoading(true);
    const payload = {
      ...(data[GST_PERCENTAGE] ? { gstNotes: data[GST_PERCENTAGE] } : {}),
      installmentNumber: data[INSTALLMENT_NUMBER],
      installmentAmountNonGst: Number(data[INSTALLMENT_AMOUNT_NON_GST]),
      installmentAmountGst: Number(data[INSTALLMENT_AMOUNT_GST]),
      invoiceAmount: Number(data[TOTAL_INVOICE_AMOUNT]),
      installmentIndex,
    };
    await updateInstallment(payload as any);
  };

  const onCancel = () => {
    const basePath = getBasePath(loggedInUserData?.role);
    navigate(`${basePath}/school/${id}/edit`);
  };

  return {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    isLoading,
    pdcData,
    isPdcCheckEnabled,
    handlePdcCheck,
    isPdcLoading,
    donwloadReceipt,
    receiptForm,
    isCRM_HEAD,
  };
};

export default useEditInstallmentSchedule;
